import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import clsx from 'clsx';
import { Badge } from '@/components/badges/Badge/Badge';
import IconArrowDown from '@/assets/icons/icon-arrow-down.svg?react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NavbarSave } from '@/components/navbar/NavbarSave/NavbarSave';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectCurrentPageId, selectUpdatePageLoading } from '@/store/pages/selectors';
import { updatePageById } from '@/store/pages/thunks';
import { selectNavbarTitleDropdownOpen } from '@/store/system/selectors';
import { setNavbarTitleDropdownOpen } from '@/store/system/systemSlice';

interface NavbarProjectTitleProps {
  badge: string;
  title: string;
  className: string;
  onClick?: () => void;
}

export const NavbarProjectTitle: React.FC<NavbarProjectTitleProps> = ({
  className,
  badge = 'DRAFT',
  title,
}) => {
  const dispatch = useAppDispatch();
  const currentPageId = useAppSelector(selectCurrentPageId);
  const loading = useAppSelector(selectUpdatePageLoading);
  const shouldOpenDropdown = useAppSelector(selectNavbarTitleDropdownOpen);
  const [draftPageName, setDraftPageNameLocal] = useState<string | null>(null);

  const [pageName, setPageName] = useState(title);
  const [displayedTitle, setDisplayedTitle] = useState(title);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [titleWidth, setTitleWidth] = useState<number | undefined>(undefined);
  const [suppressTransition, setSuppressTransition] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [hasMeasured, setHasMeasured] = useState(false);
  const savedTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setPageName(title);
    setDisplayedTitle(title);
  }, [title]);

  useEffect(() => {
    if (shouldOpenDropdown) {
      setDropdownOpen(true);
      dispatch(setNavbarTitleDropdownOpen(false));
    }
  }, [shouldOpenDropdown, dispatch]);

  const effectiveTitle = displayedTitle;

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) window.clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  const handleSave = async () => {
    if (!currentPageId || loading) return;
    const body = { name: draftPageName ?? pageName };
    try {
      dispatch(
        updatePageById({
          pageId: currentPageId,
          body,
        }),
      );
    } catch (error) {
      console.error('Failed to update page name:', error);
    }
  };

  // Truncate only if measured width exceeds threshold (150px)
  const TRUNCATE_THRESHOLD = 150;
  const shouldTruncate = hasMeasured && titleWidth !== undefined && titleWidth > TRUNCATE_THRESHOLD;
  const widthStyle =
    hasMeasured && titleWidth !== undefined
      ? { width: `${shouldTruncate ? TRUNCATE_THRESHOLD : titleWidth}px` }
      : undefined;

  // Re-measure whenever effective title changes
  useLayoutEffect(() => {
    if (measureRef.current) {
      const w = measureRef.current.getBoundingClientRect().width;
      setTitleWidth(w);
      if (!hasMeasured) setHasMeasured(true);
      requestAnimationFrame(() => setSuppressTransition(false));
    }
  }, [effectiveTitle, hasMeasured]);

  const handleDropdownOpen = (value: boolean) => setDropdownOpen(value);

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <DropdownMenu open={dropdownOpen} onOpenChange={handleDropdownOpen}>
        <DropdownMenuTrigger
          className="group flex cursor-pointer items-center gap-2 outline-none"
          data-qa-selector="navbar-page-title-dropdown-trigger"
        >
          <Badge className="" color="muted" size="small" text={badge} />
          <div
            className="relative flex min-w-0 items-center"
            style={shouldTruncate ? { maxWidth: TRUNCATE_THRESHOLD } : undefined}
          >
            {/* Measuring span (hidden) */}
            <span
              ref={measureRef}
              aria-hidden="true"
              className="pointer-events-none absolute top-0 left-0 -z-10 text-xs leading-none font-medium whitespace-nowrap opacity-0"
            >
              {effectiveTitle}
            </span>
            <span
              style={widthStyle}
              className={clsx(
                'block text-xs leading-none font-medium whitespace-nowrap text-neutral-900 duration-300 ease-in-out outline-none',
                shouldTruncate && 'truncate',
                suppressTransition ? 'transition-none' : 'transition-all',
              )}
              title={shouldTruncate ? effectiveTitle || '' : undefined}
              onTransitionEnd={() => {
                if (measureRef.current) {
                  const w = measureRef.current.getBoundingClientRect().width;
                  if (w !== titleWidth) setTitleWidth(w);
                }
              }}
            >
              {effectiveTitle}
            </span>
          </div>
          <IconArrowDown
            className={clsx(
              'h-5 w-5 text-neutral-500 transition-transform duration-200',
              dropdownOpen && 'rotate-180',
            )}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="mt-1 border-none bg-transparent p-0">
          <NavbarSave
            header="Page Name"
            value={draftPageName != null ? draftPageName : pageName}
            placeholder={title}
            onChange={(e) => {
              const v = e.target.value;
              setPageName(v);
              setDraftPageNameLocal(v);
            }}
            onSave={() => {
              handleSave();
            }}
            onSaved={() => {
              setDisplayedTitle(pageName);
              setDraftPageNameLocal(null);
              if (savedTimeoutRef.current) window.clearTimeout(savedTimeoutRef.current);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
